import React, { useCallback, useContext, useMemo, useState } from 'react'

import { DATParser } from '@asta/core'
import type { FC } from 'react'

import type {
	FormSpec,
	FormSpecData,
	FormSpecWithPopulatedParentAndTags,
} from '@/api/repository/repositorySchemas'
import type { TagWithColor } from '@/components/feature/TestAsset/utils'
import AstaDialog from '@/components/shared/AstaDialog/AstaDialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { AuthContext } from '@/contexts/auth'
import { canCreate, canEdit } from '@/pages/Admin/helpers'
import { SYSTEM_ID } from '@/pages/Admin/system-settings/constants'
import type { CreateAsset, Resource, Tag, Variant } from '@/shared/types'

import FileUploader from '../shared/FileUploader'
import { Label } from '../shared/Label'
import { EditorTagAutocomplete } from '../Tags/EditorTagAutocomplete'
import FormSpecFieldsTable from './FormSpecFieldsTable'

export const defaultSpec: FormSpecData = {
	fields: [],
	formTitle: null,
	formId: null,
	ombControlNumber: null,
	formVersion: null,
	ombExpirationDate: null,
	formFamilies: null,
}

const defaultFormSpecAsset: FormSpec = {
	_id: null,
	name: 'Default Form Spec',
	type: 'form-spec',
	description: '',
	tags: [],
	data: defaultSpec,
	parent: null,
	status: 'active',
}

interface Props {
	asset: FormSpecWithPopulatedParentAndTags
	create: (newFormSpec: CreateAsset) => void
	update: (formSpecToUpdate: FormSpec, tags?: string[]) => void
	isVisible: boolean
	onClose: () => void
	selectedApp: Variant
	isEditing?: boolean
	availableTags: TagWithColor[]
}

const FormSpecEditorContainer: FC<Props> = ({
	asset,
	create,
	update,
	isVisible,
	selectedApp,
	onClose,
	isEditing = false,
	availableTags,
}) => {
	const { user } = useContext(AuthContext)
	const [formSpec, setFormSpec] = useState<FormSpec>(
		isEditing ? asset : defaultFormSpecAsset
	)
	const [newTags, setNewTags] = useState<string[]>(
		isEditing ? (asset?.tags as Tag[])?.map(tag => tag._id) : []
	)
	const parser = useMemo(() => new DATParser(), [])
	const [_error, setError] = useState<string | null>(null)
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [showTable, setShowTable] = useState(false)

	const updateTags = useCallback(
		(updatedTags: string[]) => {
			if (isEditing) {
				update(asset, updatedTags)
			} else {
				setNewTags(updatedTags)
			}
		},
		[asset, isEditing, update]
	)

	const onFormSpecCreate = useCallback(
		(spec: FormSpec, globalAsset = false) => {
			const newFormSpec: CreateAsset = {
				...spec,
				tags: newTags,
				type: 'form-spec',
				parent: globalAsset ? SYSTEM_ID : selectedApp.parent._id,
				status: 'active',
			}
			create(newFormSpec)
			onClose()
		},
		[create, selectedApp, onClose, newTags]
	)

	const onFormSpecUpdate = useCallback(
		(spec: FormSpec) => {
			update(spec)
			onClose()
		},
		[update, onClose]
	)

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setFormSpec(prev => ({
				...prev,
				[e.target.name]: e.target.value,
			}))
		},
		[setFormSpec]
	)

	const onDataChange = useCallback(
		(data: FormSpecData) => {
			setFormSpec(prev => ({
				...prev,
				name: `${data.formTitle} [V${data.formVersion}]`,
				description: `Generated for "${data.formTitle} [V${data.formVersion}]"`,
				data,
			}))
		},
		[setFormSpec]
	)

	const parseSpec = useCallback(
		(file: File) => {
			const reader = new FileReader()
			reader.readAsArrayBuffer(file)
			reader.onload = () => {
				try {
					const parsedDocument = parser.fromXlsxFile(reader.result)
					setError(null)
					onDataChange({
						formTitle: parsedDocument.formTitle,
						formId: parsedDocument.formId,
						formVersion: parsedDocument.formVersion,
						formFamilies: parsedDocument.formFamilies,
						ombControlNumber: parsedDocument.ombControlNumber,
						ombExpirationDate: parsedDocument.ombExpirationDate,
						fields: parsedDocument.fields,
					})
					setShowTable(true)
				} catch {
					setError('Error parsing file.')
					alert('Error parsing file')
				}
			}
		},
		[onDataChange, parser]
	)

	const handleFileSelect = useCallback(
		(file: File) => {
			setSelectedFile(file)
			parseSpec(file)
		},
		[parseSpec]
	)

	const dialogTitle = useMemo(
		() => (
			<div className="flex flex-col gap-2 w-full sm:flex-row sm:items-center sm:justify-between">
				<div className="text-lg font-semibold">
					{isEditing ? `Edit ${formSpec.name}` : 'Create Form Spec'}
				</div>
				{availableTags?.length > 0 && (
					<EditorTagAutocomplete
						tags={isEditing ? (formSpec.tags as Tag[]) ?? [] : []}
						availableTags={availableTags}
						setTags={updateTags}
						disabled={
							(isEditing
								? !canEdit((formSpec.parent as Resource)?.role)
								: !canCreate(selectedApp?.role)) &&
							!user?.isAdmin
						}
					/>
				)}
			</div>
		),
		[
			availableTags,
			formSpec.name,
			formSpec.parent,
			formSpec.tags,
			isEditing,
			selectedApp?.role,
			updateTags,
			user?.isAdmin,
		]
	)

	const dialogContent = useMemo(
		() => (
			<div className="space-y-4">
				<div className="grid w-full items-center gap-1.5">
					<Label
						label="Name"
						description="Name to identify your asset"
						htmlFor="name"
					/>
					<Input
						id="name"
						name="name"
						placeholder="Name..."
						value={formSpec.name}
						onChange={onChange}
					/>
				</div>
				<div className="grid w-full items-center gap-1.5">
					<Label
						label="Description"
						description="Briefly describe your asset"
						htmlFor="description"
					/>
					<Textarea
						id="description"
						name="description"
						rows={2}
						value={formSpec.description}
						onChange={onChange}
						placeholder="Description..."
					/>
				</div>

				{isEditing || showTable ? (
					<div className="space-y-2">
						<Label
							label="Fields"
							description="Requirements for each field"
						/>
						<div className="max-h-[400px] overflow-y-auto rounded-md border">
							<FormSpecFieldsTable
								spec={formSpec.data as FormSpecData}
							/>
						</div>
					</div>
				) : (
					<FileUploader
						file={selectedFile}
						setFile={handleFileSelect}
					/>
				)}
			</div>
		),
		[
			formSpec.data,
			formSpec.description,
			formSpec.name,
			handleFileSelect,
			isEditing,
			onChange,
			selectedFile,
			showTable,
		]
	)

	const dialogActions = useMemo(
		() => [
			{
				text: isEditing ? 'Save' : 'Create',
				variant: 'default' as const,
				onClick: () =>
					isEditing
						? onFormSpecUpdate(formSpec)
						: onFormSpecCreate(formSpec),
				disabled:
					(isEditing
						? !canEdit((asset?.parent as Resource)?.role)
						: !canCreate(selectedApp?.role)) && !user?.isAdmin,
			},
			...(!isEditing && user?.isAdmin
				? [
						{
							text: 'Save as global asset',
							variant: 'success' as const,
							onClick: () => onFormSpecCreate(formSpec, true),
						},
					]
				: []),
		],
		[
			asset?.parent,
			formSpec,
			isEditing,
			onFormSpecCreate,
			onFormSpecUpdate,
			selectedApp?.role,
			user?.isAdmin,
		]
	)

	if (!isVisible || !formSpec) return null

	return (
		<AstaDialog
			onClose={onClose}
			show={isVisible}
			setShow={onClose}
			title={dialogTitle}
			content={dialogContent}
			actions={dialogActions}
		/>
	)
}

export default FormSpecEditorContainer
